import { nanoid } from "nanoid"
import { paletteByKey } from "@/utils/colorSlots"
import { resolveFormColors } from "@/utils/formColors"
import Logger from "@/utils/Logger"

interface UseFormConfigOptions {
  /** Locale to inject into the form config if the API doesn't provide one (e.g. jobShop.locale) */
  locale?: string
  /** The JobShop whose palette the form colours resolve against (JET-2975) */
  jobShop?: JobShop | null
}

/**
 * Composable to fetch form configuration for a job application
 *
 * @param jobId The ID of the job offer
 * @param groupId The ID of the group
 * @param options Optional enrichment options (locale, etc.)
 * @returns Form configuration data and status
 */
export async function useFormConfig(jobId: string | null, groupId: string | null, options: UseFormConfigOptions = {}) {
  const { locale, jobShop } = options
  const { $api } = useNuxtApp()
  const { tenantId, jobShopId, acceptLanguage } = useHeaderStore()

  // If no jobId is provided, return null for the form config
  if (!jobId) {
    return {
      formConfig: computed(() => null),
      formConfigStatus: ref("idle"),
      formConfigError: ref(null),
    }
  }

  const requestId = nanoid()
  const contextString = `form configuration for jobId: "${jobId}"${groupId ? `, groupId: "${groupId}"` : ""}, tenantId: "${tenantId}" and jobShopId: "${jobShopId}"`

  let startTime = 0
  if (import.meta.server) {
    Logger(requestId).start(`Start fetching ${contextString}`)
    startTime = Date.now()
  }

  // Using useAsyncData without caching for now
  const {
    data: rawFormConfig,
    status: formConfigStatus,
    error: formConfigError,
  } = await useAsyncData<FormConfig>(
    `formConfig-${jobId}`,
    async () => {
      try {
        const response = await $api.form.getFormConfig(tenantId, jobShopId, acceptLanguage, jobId, groupId)

        // Validate the response
        if (typeof response === "string" && response.trim().startsWith("<!DOCTYPE html>"))
          throw new Error("Endpoint returned HTML error page instead of JSON response")

        if (!response || typeof response !== "object")
          throw new Error("Invalid response format from form endpoint")

        // Resolved here rather than in the computed below: pages/success.vue reads this
        // payload directly through useNuxtData.
        return resolveFormColors(response as FormConfig, paletteByKey(jobShop))
      }
      catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error"
        console.error(`Error fetching form config: ${errorMessage}`)
        throw error
      }
    },
  )

  // Create a computed ref that validates the form config
  const formConfig = computed<FormConfig | null>(() => {
    if (!rawFormConfig.value)
      return null
    if (typeof rawFormConfig.value === "string")
      return null
    if (!rawFormConfig.value.form)
      return null
    if (locale && !rawFormConfig.value.form.locale)
      return { ...rawFormConfig.value, form: { ...rawFormConfig.value.form, locale } }

    return rawFormConfig.value
  })

  if (import.meta.server) {
    if (!formConfigError.value) {
      const duration = Date.now() - startTime
      Logger(requestId).success(`Successfully fetched ${contextString}. (${duration}ms)`)
    }
    else {
      const statusCode = formConfigError.value?.statusCode || "unknown"
      const errorMessage = formConfigError.value?.message || "Unknown error occurred"
      Logger(requestId).error(`Error while fetching ${contextString}: ${errorMessage} (Status: ${statusCode})`)
    }
  }

  return { formConfig, formConfigStatus, formConfigError }
}
